import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommunicationPanel = ({ 
  clientData,
  workOrderData,
  onSendEmail,
  onMakeCall,
  onSendWhatsApp 
}) => {
  const [activeTab, setActiveTab] = useState('email');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');

  const handleSendEmail = () => {
    if (emailSubject?.trim() && emailBody?.trim()) {
      onSendEmail({
        to: clientData?.email,
        subject: emailSubject,
        body: emailBody,
        workOrderId: workOrderData?.id,
        timestamp: new Date()?.toISOString()
      });
      setEmailSubject('');
      setEmailBody('');
    }
  };

  const handleSendWhatsApp = () => {
    if (whatsappMessage?.trim()) {
      onSendWhatsApp({
        to: clientData?.telefono,
        message: whatsappMessage,
        workOrderId: workOrderData?.id,
        timestamp: new Date()?.toISOString()
      });
      setWhatsappMessage('');
    }
  };

  const tabs = [
    { id: 'email', label: 'Email', icon: 'Mail' },
    { id: 'phone', label: 'Teléfono', icon: 'Phone' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
        <div className="p-2 rounded-lg bg-accent/10">
          <Icon name="MessageSquare" size={24} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Comunicación con Cliente
          </h3>
          <p className="text-xs md:text-sm caption text-muted-foreground">
            {clientData?.nombre}
          </p>
        </div>
      </div>
      <div className="flex gap-2 mb-4 md:mb-6 overflow-x-auto pb-2">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-body font-medium text-sm
              transition-smooth press-effect focus-ring whitespace-nowrap flex-shrink-0
              ${activeTab === tab?.id 
                ? 'bg-primary text-primary-foreground shadow-elevation-1' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }
            `}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      <div className="space-y-4 md:space-y-6">
        {activeTab === 'email' && (
          <div className="space-y-3 md:space-y-4">
            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-2">
                Para:
              </label>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                <Icon name="Mail" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {clientData?.email || 'No hay email registrado'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-2">
                Asunto:
              </label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e?.target?.value)}
                placeholder="Actualización de su orden de trabajo"
                className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-border bg-background text-foreground text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary transition-smooth"
              />
            </div>

            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-2">
                Mensaje:
              </label>
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e?.target?.value)}
                placeholder={`Estimado/a ${clientData?.nombre},\n\nLe informamos sobre el estado de su vehículo...`}
                rows={6}
                className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-border bg-background text-foreground text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
              />
            </div>

            <Button
              variant="default"
              iconName="Send"
              iconPosition="left"
              onClick={handleSendEmail}
              disabled={!clientData?.email || !emailSubject?.trim() || !emailBody?.trim()}
              fullWidth
            >
              Enviar Email
            </Button>
          </div>
        )}

        {activeTab === 'phone' && (
          <div className="space-y-4 md:space-y-6">
            <div className="p-4 md:p-6 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center gap-3 mb-3 md:mb-4">
                <Icon name="Phone" size={24} className="text-primary" />
                <div>
                  <p className="text-sm md:text-base font-body font-semibold text-foreground">
                    Teléfono Principal
                  </p>
                  <p className="text-lg md:text-xl font-heading font-bold text-primary">
                    {clientData?.telefono}
                  </p>
                </div>
              </div>
              
              <Button
                variant="default"
                iconName="Phone"
                iconPosition="left"
                onClick={() => onMakeCall(clientData?.telefono)}
                fullWidth
              >
                Llamar Ahora
              </Button>
            </div>

            {clientData?.telefonoAlt && (
              <div className="p-4 md:p-6 rounded-lg bg-muted/30 border border-border">
                <div className="flex items-center gap-3 mb-3 md:mb-4">
                  <Icon name="Phone" size={24} className="text-secondary" />
                  <div>
                    <p className="text-sm md:text-base font-body font-semibold text-foreground">
                      Teléfono Alternativo
                    </p>
                    <p className="text-lg md:text-xl font-heading font-bold text-secondary">
                      {clientData?.telefonoAlt}
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="secondary"
                  iconName="Phone"
                  iconPosition="left"
                  onClick={() => onMakeCall(clientData?.telefonoAlt)}
                  fullWidth
                >
                  Llamar Alternativo
                </Button>
              </div>
            )}

            <div className="p-3 md:p-4 rounded-lg bg-accent/10 border border-accent/20">
              <div className="flex items-start gap-2 md:gap-3">
                <Icon name="Info" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm md:text-base font-body font-medium text-foreground mb-1">
                    Información de la Orden
                  </p>
                  <p className="text-xs md:text-sm caption text-muted-foreground">
                    {workOrderData?.folioOT} - {workOrderData?.unidad?.marca} {workOrderData?.unidad?.modelo}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'whatsapp' && (
          <div className="space-y-3 md:space-y-4">
            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-2">
                Para:
              </label>
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
                <Icon name="MessageCircle" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">
                  {clientData?.telefono}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-body font-medium text-foreground mb-2">
                Mensaje:
              </label>
              <textarea
                value={whatsappMessage}
                onChange={(e) => setWhatsappMessage(e?.target?.value)}
                placeholder={`Hola ${clientData?.nombre}, le contactamos sobre su ${workOrderData?.unidad?.marca} ${workOrderData?.unidad?.modelo}...`}
                rows={6}
                className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-border bg-background text-foreground text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
              />
            </div>

            <Button
              variant="success"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={handleSendWhatsApp}
              disabled={!whatsappMessage?.trim()}
              fullWidth
            >
              Enviar por WhatsApp
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationPanel;